/**
 * Department — the shared organizational scope Eadin owns and KIP consumes.
 *
 * Three separate concepts, deliberately not collapsed into one field:
 *   Department                — where a role belongs (below).
 *   Company-Wide Governance   — how broadly its access applies (a flag on the role; it only ever
 *                               widens scope, and never grants authority by itself).
 *   Governance permission     — what the role may do (none | view | edit, below).
 *
 * A role belongs to exactly one department, and a user belongs to exactly one role, so the
 * department is a property of the human's role, never of a KIP resource type. KIP tags its own
 * resources (governance documents, memory, decisions, exceptions, approvals) with a department
 * and asks Eadin what the acting employee's department, breadth and governance authority are.
 * Role names are never special-cased on either side, so an org can have many Customer Care roles
 * at different levels without a code change in KIP.
 */

const {
  CHILDREN_TAB_MEC_EVIDENCE_IMPORTS,
  CHILDREN_TAB_MEC_JE_POSTING,
} = require('../management/users/users');

const DEPARTMENTS = Object.freeze([
  Object.freeze({ value: 'customer_care', label: 'Customer Care' }),
  Object.freeze({ value: 'finance', label: 'Finance' }),
  Object.freeze({ value: 'marketing', label: 'Marketing' }),
  Object.freeze({ value: 'creative', label: 'Creative' }),
  Object.freeze({ value: 'retention', label: 'Retention' }),
  Object.freeze({ value: 'merchandising', label: 'Merchandising' }),
  Object.freeze({ value: 'operations', label: 'Operations' }),
  Object.freeze({ value: 'supply_chain', label: 'Supply Chain' }),
  Object.freeze({ value: 'product_development', label: 'Product Development' }),
  Object.freeze({ value: 'ecommerce', label: 'eCommerce' }),
  Object.freeze({ value: 'amazon', label: 'Amazon' }),
  Object.freeze({ value: 'executive_leadership', label: 'Executive / Leadership' }),
  Object.freeze({ value: 'people_hr', label: 'People / HR' }),
  Object.freeze({ value: 'technology_engineering', label: 'Technology / Engineering' }),
]);

const DEPARTMENT_VALUES = Object.freeze(DEPARTMENTS.map((department) => department.value));

const DEPARTMENT_LABELS = Object.freeze(
  DEPARTMENTS.reduce((labels, department) => {
    labels[department.value] = department.label;
    return labels;
  }, {})
);

/** Accepts a stored value or a human label and returns the canonical value, else null. */
const normalizeDepartment = (department) => {
  if (!department) return null;
  const raw = String(department).trim();
  if (!raw) return null;
  const asValue = raw.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
  if (DEPARTMENT_VALUES.includes(asValue)) return asValue;
  const byLabel = DEPARTMENTS.find(
    (item) => item.label.toLowerCase() === raw.toLowerCase()
  );
  return byLabel ? byLabel.value : null;
};

const isValidDepartment = (department) => normalizeDepartment(department) !== null;

const departmentLabel = (department) => {
  const value = normalizeDepartment(department);
  return value ? DEPARTMENT_LABELS[value] : null;
};

/**
 * Governance permission — the only permission in Eadin not derived from a navigable page, because
 * governance lives in KIP, not in the Eadin sidebar. Stored in the same nested role-permission
 * shape as every other permission (`[{ governance: [{ governance: 'edit' }] }]`) so it rides the
 * existing role read/write path and the existing `hasChildAccess` semantics.
 *
 *   none — no governance visibility.
 *   view — read the department's governance and proposals.
 *   edit — read, refine and approve them.
 */
const GOVERNANCE_PERMISSION_TAB = 'governance';
const GOVERNANCE_PERMISSION_CHILD = 'governance';
const GOVERNANCE_ACCESS_NONE = 'none';
const GOVERNANCE_ACCESS_VIEW = 'view';
const GOVERNANCE_ACCESS_EDIT = 'edit';
const GOVERNANCE_ACCESS_VALUES = Object.freeze([
  GOVERNANCE_ACCESS_NONE,
  GOVERNANCE_ACCESS_VIEW,
  GOVERNANCE_ACCESS_EDIT,
]);

/**
 * Synthetic catalog entry for the role editor. Shaped exactly like `getPermissionCatalog()`
 * entries so the drawer renders it with the same None/View/Edit control, but with no route.
 */
const NON_ROUTE_PERMISSION_CATALOG = Object.freeze([
  Object.freeze({
    permissionId: GOVERNANCE_PERMISSION_CHILD,
    routePermissionId: null,
    parentId: GOVERNANCE_PERMISSION_TAB,
    sectionId: GOVERNANCE_PERMISSION_TAB,
    sectionLabel: 'GOVERNANCE',
    modulePath: [],
    label: 'Governance',
    path: null,
  }),
  Object.freeze({
    permissionId: 'location-capacity',
    routePermissionId: null,
    parentId: 'fulfillment',
    sectionId: 'fulfillment',
    sectionLabel: 'FULFILLMENT',
    modulePath: ['Location Master'],
    label: 'Location Capacity',
    path: null,
  }),
  Object.freeze({
    permissionId: 'location-capacity-sheet-sync',
    routePermissionId: null,
    parentId: 'fulfillment',
    sectionId: 'fulfillment',
    sectionLabel: 'FULFILLMENT',
    modulePath: ['Location Master'],
    label: 'Capacity Sheet Sync',
    path: null,
  }),
  Object.freeze({
    permissionId: CHILDREN_TAB_MEC_JE_POSTING,
    routePermissionId: null,
    parentId: 'finance',
    sectionId: 'finance',
    sectionLabel: 'FINANCE',
    modulePath: [],
    label: 'MEC JE Posting',
    path: null,
  }),
  Object.freeze({
    permissionId: CHILDREN_TAB_MEC_EVIDENCE_IMPORTS,
    routePermissionId: null,
    parentId: 'finance',
    sectionId: 'finance',
    sectionLabel: 'FINANCE',
    modulePath: [],
    label: 'MEC Evidence Imports',
    path: null,
  }),
]);

/** Read the governance access level out of a role's stored permissions array. */
const governanceAccessFromPermissions = (permissions) => {
  for (const group of Array.isArray(permissions) ? permissions : []) {
    const [tab, children] = Object.entries(group || {})[0] || [];
    if (tab !== GOVERNANCE_PERMISSION_TAB || !Array.isArray(children)) continue;
    for (const entry of children) {
      const [child, access] = Object.entries(entry || {})[0] || [];
      if (child === GOVERNANCE_PERMISSION_CHILD && GOVERNANCE_ACCESS_VALUES.includes(access)) {
        return access;
      }
    }
  }
  return GOVERNANCE_ACCESS_NONE;
};

module.exports = {
  DEPARTMENTS,
  DEPARTMENT_VALUES,
  DEPARTMENT_LABELS,
  normalizeDepartment,
  isValidDepartment,
  departmentLabel,
  GOVERNANCE_PERMISSION_TAB,
  GOVERNANCE_PERMISSION_CHILD,
  GOVERNANCE_ACCESS_NONE,
  GOVERNANCE_ACCESS_VIEW,
  GOVERNANCE_ACCESS_EDIT,
  GOVERNANCE_ACCESS_VALUES,
  NON_ROUTE_PERMISSION_CATALOG,
  governanceAccessFromPermissions,
};
